import { setIsLoading } from '../app/Actions/cmsAction';

// Load OneDrive SDK dynamically
export const loadOneDriveSDK = () => {
  return new Promise((resolve, reject) => {
    if (window.OneDrive) {
      resolve(window.OneDrive);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.live.net/v7.2/OneDrive.js';
    script.async = true;
    script.onload = () => resolve(window.OneDrive);
    script.onerror = () => reject(new Error('Failed to load OneDrive SDK'));
    document.body.appendChild(script);
  });
};

// Custom hook for OneDrive integration
export const useOneDrivePicker = (dispatch) => {
  const openPicker = async ({ onSelect }) => {
    try {
      // await loadOneDriveSDK();
      dispatch(setIsLoading(true));
      
      const odOptions = {
        clientId: "3c779ca3-e2a5-490d-90a8-fb6082b87a5a", // Replace with your app's client ID
        action: "download",
        multiSelect: true,
        advanced: {
          filter: ".mp4,.mov,.wmv,.avi,.mkv", // Filter for video files
          redirectUri: `${window.location.protocol}//${window.location.hostname}${window.location.port ? ":" + window.location.port : ""}`
        },
        success: async (response) => {
          try {
            // Ensure response has the expected format
            // IMPORTANT: The structure of the OneDrive response might be different
            // Check the console log below to see the actual structure
            console.log('OneDrive response:', response);
            
            // Handle different possible response formats
            const filesArray = response?.value || // Graph API format
                              (Array.isArray(response) ? response : [response]); // Other possible formats
            
            if (!filesArray || filesArray.length === 0) {
              console.warn('No files returned from OneDrive');
              dispatch(setIsLoading(false));
              return;
            }
            
            const filesToDownload = filesArray.map(file => {
              // Ensure all required properties exist
              if (!file) return null;
              
              // Handle different possible file structures
              const downloadUrl = file["@microsoft.graph.downloadUrl"] || 
                                 file.downloadUrl || 
                                 file.webUrl;
              
              if (!downloadUrl) {
                console.warn('Missing download URL for file:', file);
                return null;
              }
              
              return {
                downloadUrl,
                name: file.name || 'unknown_file',
                bytes: file.size || 0,
                id: file.id || `file_${Date.now()}`,
                mimeType: getMimeTypeFromFileName(file.name || '')
              };
            }).filter(file => file !== null); // Remove any null entries
            
            if (filesToDownload && filesToDownload.length > 0) {
              onSelect(filesToDownload);
            } else {
              console.warn('No valid files to download after processing');
            }
          } catch (error) {
            console.error('Error processing OneDrive files:', error);
          } finally {
            dispatch(setIsLoading(false));
          }
        },
        cancel: () => {
          dispatch(setIsLoading(false));
          console.log("User canceled the OneDrive picker");
        },
        error: (error) => {
          dispatch(setIsLoading(false));
          console.error("Error with OneDrive picker:", error);
        },
      };
      
      window.OneDrive.open(odOptions);
    } catch (error) {
      console.error("Failed to initialize OneDrive picker:", error);
      dispatch(setIsLoading(false));
    }
  };
  
  return { openPicker };
};

// Download file from OneDrive URL
export const downloadFileFromOneDrive = async (fileInfo) => {
  try {
    if (!fileInfo || !fileInfo.downloadUrl) {
      throw new Error('Invalid file information or missing download URL');
    }
    
    const response = await fetch(fileInfo.downloadUrl);
    if (!response.ok) {
      throw new Error(`Failed to download file from OneDrive: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    // Create a File object from the blob
    return new File([blob], fileInfo.name || 'downloaded_file', {
      type: fileInfo.mimeType || getMimeTypeFromFileName(fileInfo.name || ''),
      lastModified: new Date().getTime()
    });
  } catch (error) {
    console.error('Error downloading file from OneDrive:', error);
    throw error;
  }
};

// Helper function to guess MIME type from filename
function getMimeTypeFromFileName(filename) {
  if (!filename) return 'application/octet-stream';
  
  const extension = filename.split('.').pop()?.toLowerCase() || '';
  const mimeTypes = {
    'mp4': 'video/mp4',
    'mov': 'video/quicktime',
    'wmv': 'video/x-ms-wmv',
    'avi': 'video/x-msvideo',
    'mkv': 'video/x-matroska',
    'webm': 'video/webm'
  };
  
  return mimeTypes[extension] || 'application/octet-stream';
}